"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import {useId, useRef, useState, type KeyboardEvent} from "react";
import {useTranslation} from "react-i18next";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import type {SavedOmegaWorkflow} from "@/services/wingman/omega-results";
import JsonDisclosure from "./JsonDisclosure";
import CompiledWorkflowPublicationOutcome from "./CompiledWorkflowPublicationOutcome";
import {workflowBuilderPath} from "@/utils/common/constants/routes";
import {confirmedDraftOf, type ChatWorkflowPublication, type ConfirmedWorkflowDraft} from "./useChatWorkflowPublication";
import {useCurrentChatWorkflow} from "./useCurrentChatWorkflow";

const OmegaGraphPreview = dynamic(() => import("./OmegaGraphPreview"), {ssr: false});

type Props = {
   workflow: SavedOmegaWorkflow;
   openInEditor: boolean;
   onOpenInEditor?: () => void;
   unavailable: boolean;
   onTest: (id: string) => Promise<boolean>;
   onPublish: (draft: ConfirmedWorkflowDraft) => Promise<ChatWorkflowPublication>;
};

type Publication = ChatWorkflowPublication | {status: "publishing"};

/** The attempt did not finish, so the reviewed draft stays open to retry. */
function keepsPublishReviewOpen(status: ChatWorkflowPublication["status"]): boolean {
   return status === "canvas_not_saved" || status === "lookup_failed";
}

/** One saved workflow: its graph, and the explicit Test and Publish actions. */
export default function CompiledWorkflowCard({workflow: history, openInEditor, onOpenInEditor, unavailable, onTest, onPublish}: Props) {
   const {t} = useTranslation("ask");
   const {workflow, loading, failed, update, retry} = useCurrentChatWorkflow(history);
   const [testing, setTesting] = useState(false);
   const [testError, setTestError] = useState("");
   // Frozen when the confirmation opens; later saves in the stream never replace it.
   const [confirming, setConfirming] = useState<ConfirmedWorkflowDraft>();
   const [publication, setPublication] = useState<Publication>();
   const confirmationId = useId();
   const publishButton = useRef<HTMLButtonElement>(null);
   const isPublished = workflow.status === "published";
   const isPublishing = publication?.status === "publishing";
   const publishLabel = isPublishing ? "workflowPanel.confirm.publishing" : confirming ? "workflowPanel.confirm.submit" : "workflowPanel.publish";

   const cancelPublication = () => {
      setConfirming(undefined);
      publishButton.current?.focus();
   };

   const handlePublishKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Escape" && confirming && !isPublishing) {
         event.preventDefault();
         event.stopPropagation();
         cancelPublication();
      }
      // Holding Enter must not both open the confirmation and publish.
      if (event.repeat && (event.key === "Enter" || event.key === " ")) event.preventDefault();
   };

   const requestTest = async () => {
      setTesting(true);
      setTestError("");
      setConfirming(undefined);
      try {
         if (!(await onTest(workflow.id))) setTestError(t("workflowPanel.testNotStarted"));
      } catch {
         setTestError(t("workflowPanel.testNotStarted"));
      } finally {
         setTesting(false);
      }
   };

   const publish = async (draft: ConfirmedWorkflowDraft) => {
      setPublication({status: "publishing"});
      const result = await onPublish(draft).catch((): ChatWorkflowPublication => ({status: "publish_failed"}));
      setPublication(result);
      if (result.status === "published" || result.status === "already_published") {
         update({...confirmedDraftOf(result.workflow), status: "published"});
      }
      if (result.status === "stale") {
         if (result.current) update({...result.current, status: "draft"});
         setConfirming(result.current ?? undefined);
      } else if (!keepsPublishReviewOpen(result.status)) {
         setConfirming(undefined);
      }
   };

   let statusKey = isPublished ? "workflowPanel.status.published" : "workflowPanel.status.draft";
   if (failed) statusKey = "workflowPanel.status.unavailable";
   if (loading) statusKey = "workflowPanel.status.checking";

   return (
      <article className="space-y-3 rounded-lg border border-300 bg-panel p-3">
         <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
               <h4 className="break-words text-sm font-semibold text-900">{workflow.name}</h4>
               <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-500">
                  <Badge variant={isPublished ? "default" : "secondary"}>
                     {t(statusKey)}
                  </Badge>
                  <span>{t("workflowPanel.revision", {revision: workflow.revision})}</span>
                  <span>{t("workflowPanel.steps", {count: workflow.graph.nodes.length})}</span>
               </div>
            </div>
            <div className="flex flex-wrap gap-2">
               {openInEditor && (
                  <Button asChild variant="outline" size="sm">
                     <Link href={workflowBuilderPath(workflow.id)} onNavigate={onOpenInEditor}>{t("workflowPanel.openInEditor")}</Link>
                  </Button>
               )}
            </div>
         </div>
         {workflow.description && <p className="text-sm text-600">{workflow.description}</p>}
         <OmegaGraphPreview graph={confirming?.graph ?? workflow.graph} />
         <JsonDisclosure label={t("workflowPanel.graphData")} value={confirming ?? workflow} />
         <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={unavailable || loading || failed || isPublishing || testing} onClick={() => void requestTest()}>
               {t(testing ? "workflowPanel.testing" : "workflowPanel.test")}
            </Button>
            {workflow.status === "draft" && (
               <Button
                  ref={publishButton}
                  size="sm"
                  aria-expanded={!!confirming}
                  aria-controls={confirming ? confirmationId : undefined}
                  aria-describedby={confirming ? confirmationId : undefined}
                  disabled={unavailable || isPublishing || testing || (!confirming && (loading || failed || !Number.isInteger(Number(workflow.revision))))}
                  onKeyDown={handlePublishKeyDown}
                  onClick={(event) => {
                     // A double-click should open the review, never skip it.
                     if (event.detail > 1) return;
                     if (confirming) {
                        void publish(confirming);
                     } else {
                        setConfirming(confirmedDraftOf(workflow));
                        setPublication(undefined);
                     }
                  }}
               >
                  {/* Reserve every label's width so the click target never moves. */}
                  <span className="grid">
                     {["workflowPanel.publish", "workflowPanel.confirm.submit", "workflowPanel.confirm.publishing"].map(key => (
                        <span key={key} className={`col-start-1 row-start-1 ${key === publishLabel ? "" : "invisible"}`} aria-hidden={key !== publishLabel}>
                           {t(key)}
                        </span>
                     ))}
                  </span>
               </Button>
            )}
            {confirming && (
               <Button variant="ghost" size="sm" disabled={isPublishing} onKeyDown={handlePublishKeyDown} onClick={cancelPublication}>
                  {t("workflowPanel.confirm.cancel")}
               </Button>
            )}
         </div>
         <p className="text-xs text-500">{t("workflowPanel.testHint")}</p>
         {testError && <p role="alert" className="text-sm text-destructive">{testError}</p>}
         {confirming && (
            <section id={confirmationId} className="space-y-1 border-l-2 border-primary/30 py-1 pl-3" aria-label={t("workflowPanel.confirm.label")} aria-live="polite">
               <h5 className="break-words text-xs font-medium text-900">{t("workflowPanel.confirm.title", {name: confirming.name})}</h5>
               <p className="text-xs leading-relaxed text-600">{t("workflowPanel.confirm.body")}</p>
            </section>
         )}
         {failed && <div className="space-y-2">
            <p role="alert" className="text-sm text-destructive">{t("workflowPanel.publication.lookupFailed")}</p>
            <Button variant="outline" size="sm" onClick={retry}>{t("workflowPanel.retryStatus")}</Button>
         </div>}
         {publication && publication.status !== "publishing" && <CompiledWorkflowPublicationOutcome publication={publication} />}
      </article>
   );
}
